import { CheckCircle2, AlertCircle } from "lucide-react";
import { Link } from "react-router";
import { useTaal } from "@/contexts/TaalContext";

/**
 * What happened when you asked it to remember something.
 *
 * This exists because of one evening. On 29 July Frank typed two
 * instructions, neither matched the words the detector knows, nothing was
 * stored, and nothing on screen said so. He believed both facts were
 * remembered and stopped repeating them. A memory that fails quietly is worse
 * than one that refuses out loud, so both outcomes end in a sentence here.
 *
 * The failing case carries the sentence that does work, not an apology: the
 * point is that he ends up with the thing stored, not that he is told it went
 * wrong.
 */
export default function MemoryNotice({ notice }) {
  const { t } = useTaal();
  if (!notice?.kind) return null;

  // Besluit Frank 11 oktober 2026: wat langer is dan 2000 tekens wordt een
  // document in de bank "Geheugen". Dat is opgeslagen, maar geen regel, dus
  // een eigen vorm met de zin van de server en een weg naar Knowledge.
  if (notice.kind === "document") {
    return (
      <div className="ml-12 max-w-prose rounded-lg border border-dashed p-3"
           data-testid="memory-notice-document">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">{t("Opgeslagen als document")}</p>
            <p className="mt-0.5 break-words text-sm">{notice.text}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              <Link to="/knowledge" className="underline underline-offset-2">
                {t("Bekijk het onder Knowledge")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stored = notice.kind === "stored";
  const Icon = stored ? CheckCircle2 : AlertCircle;

  return (
    <div
      className="ml-12 max-w-prose rounded-lg border border-dashed p-3"
      data-testid={stored ? "memory-notice-stored" : "memory-notice-not-stored"}
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          {stored ? (
            <>
              <p className="text-xs text-muted-foreground">{t("Onthouden")}</p>
              <p className="mt-0.5 break-words text-sm">{notice.text}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                <Link to="/memory" className="underline underline-offset-2">
                  {t("Bekijk of vergeet het in Geheugen")}
                </Link>
              </p>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">{t("Er is niets onthouden")}</p>
              <p className="mt-0.5 break-words text-sm">
                {t("Dat klonk alsof je wilde dat ik iets onthield, maar ik heb niets opgeslagen.")}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">{t(notice.how)}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
